import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton,
  Typography,
  Chip,
  Box,
  Stack,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import { useTheme } from "@emotion/react";
import RemoveRedEyeRoundedIcon from "@mui/icons-material/RemoveRedEyeRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import { useOutletContext } from "react-router-dom";
import { textTransform } from "@mui/system";
// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    fontWeight: 500,
    fontSize: "16px",
    textTransform: "capitalize",
    letterSpacing: "0.5px",
    "&:first-of-type": {
      borderTopLeftRadius: "12px",
    },
    "&:last-of-type": {
      borderTopRightRadius: "12px",
    },
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "0.875rem",
    padding: "12px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
    boxShadow: theme.shadows[1],
    transition: "all 0.2s ease-in-out",
  },
  "&:last-child td": {
    borderBottom: 0,
  },
}));

const OrderNumberCell = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
});

function createData(
  Date,
  SimulationName,
  SimulationCode,
  Cost,
  StudentCount,
  OrderNumber
) {
  return {
    Date,
    SimulationName,
    SimulationCode,
    Cost,
    StudentCount,
    OrderNumber,
  };
}

const rows = [
  createData(
    "Dec 11, 2024",
    "Retail-Simulation",
    "CBN-1012",
    "$5,000.00",
    "50",
    "AD012900038947EIN"
  ),
  createData(
    "Dec 12, 2024",
    "Retail-Simulation",
    "CBN-1012",
    "$3,500.00",
    "20",
    "AD012900038948EIN"
  ),
  createData(
    "Dec 13, 2024",
    "Retail-Simulation",
    "CBN-1012",
    "$4,200.00",
    "65",
    "AD012900038949EIN"
  ),
  createData(
    "Dec 14, 2024",
    "Retail-Simulation",
    "CBN-1012",
    "$6,000.00",
    "72",
    "AD012900038950EIN"
  ),
  createData(
    "Dec 15, 2024",
    "Retail-Simulation",
    "CBN-1012",
    "$2,800.00",
    "29",
    "AD012900038951EIN"
  ),
  createData(
    "Dec 16, 2024",
    "Retail-Simulation",
    "CBN-1012",
    "$4,500.00",
    "49",
    "AD012900038952EIN"
  ),
];

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: (theme.vars ?? theme).palette.text.secondary,
  ...theme.applyStyles("dark", {
    backgroundColor: "#1A2027",
  }),
}));
const PaymentDetailsIndex = () => {
  const theme = useTheme();
  const { drawerOpen } = useOutletContext();
  const drawerWidth = 10;
  const collapsedWidth = 240;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          marginTop: "0px",
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          marginLeft: `${drawerOpen ? drawerWidth : collapsedWidth}px`,
          ...(!drawerOpen && {
            transition: theme.transitions.create("margin", {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }),
        }}
      >
        <Box>
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h3"
              fontWeight="bold"
              gutterBottom
              sx={{
                // background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                background: theme.palette.secondary.main,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                display: "inline-block",
                letterSpacing: "-0.5px",
              }}
            >
              Invoice and billing history
            </Typography>

            <Typography
              variant="body1"
              color="#002147"
              sx={{
                maxWidth: "800px",
                lineHeight: "1.7",
                fontSize: "1.1rem",
                mt: 1,
              }}
            >
              Review your billing history and manage your invoices.
            </Typography>
          </Box>
          <Paper sx={{ p: 2 }}>
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: "12px",
                boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
                overflow: "hidden",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Table sx={{ minWidth: 700 }} aria-label="payment details table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Date</StyledTableCell>
                    <StyledTableCell align="center">
                      Simulation Name
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      Simulation Code
                    </StyledTableCell>
                    <StyledTableCell align="center">Cost</StyledTableCell>
                    <StyledTableCell align="center">
                      Student Count
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      Order Number
                    </StyledTableCell>

                    <StyledTableCell align="center">Actions</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => (
                    <StyledTableRow hover key={index}>
                      <StyledTableCell>
                        <Typography fontWeight="medium">{row.Date}</Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Typography fontWeight="medium" color="#1976d2">
                          {row.SimulationName}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Typography fontWeight="medium" color="#1976d2">
                          <Chip
                            label={row.SimulationCode}
                            size="small"
                            sx={{
                              backgroundColor: "background.default",
                              fontWeight: 500,
                              height: "24px",
                              mr: 2,
                            }}
                          />
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Typography fontWeight="medium">{row.Cost}</Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography fontWeight="medium" sx={{ ml: 1 }}>
                            {row.StudentCount}
                          </Typography>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <OrderNumberCell>
                          <Typography
                            fontWeight="medium"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {row.OrderNumber}
                          </Typography>
                        </OrderNumberCell>
                      </StyledTableCell>

                      <StyledTableCell align="center">
                        <Tooltip
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems={"center"}
                            color={"#80278c"}
                          >
                            <Item color="#80278c">
                              <IconButton size="small" color="#80278c">
                                <RemoveRedEyeRoundedIcon
                                  fontSize="small"
                                  color="#80278c"
                                />
                              </IconButton>
                            </Item>
                            <Item>
                              <IconButton size="small">
                                <FileDownloadOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Item>
                            <Item>
                              <IconButton size="small">
                                <MailOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Item>
                          </Stack>
                        </Tooltip>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            {/* <InfoOutlinedIcon color="action" fontSize="small" />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Showing {rows.length} of {rows.length} payments
            </Typography> */}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PaymentDetailsIndex;
